import { Component } from "@theme/component";
import { ThemeEvents, CartAddEvent, CartUpdateEvent, VariantUpdateEvent } from "@theme/events";
import { fetchConfig, getCart, shouldSkipBuyNowCartSync } from "@theme/utilities";
import { morph } from "@theme/morph";

/**
 * Sticky bar component for Product Display Page
 * Desktop: Shows at top when main ATC button is hidden
 * Mobile: Always visible at bottom
 */

/**
 * Format shiksha badge price with a smaller currency symbol.
 * @param {string} text
 * @returns {string}
 */
function syncMrpLabel(sourceRoot, currentRoot, kind, anchorSelector) {
  if (!sourceRoot || !currentRoot) return;
  const selector = kind === "leading" ? ".mrp-label-pdp--leading" : ".mrp-label-pdp--compare";
  const source = sourceRoot.querySelector(selector);
  const current = currentRoot.querySelector(selector);
  if (source && current) return;
  if (!source && current) {
    current.remove();
    return;
  }
  const anchor = currentRoot.querySelector(anchorSelector);
  if (source && anchor) anchor.insertAdjacentElement("beforebegin", source.cloneNode(true));
}

function formatShikshaBadgePrice(text) {
  const raw = (text || "").trim().replace(/\s/g, "");
  if (!raw) return "";
  if (raw.includes("₹")) {
    return `<span class="shiksha-price-badge__currency">₹</span><span class="shiksha-price-badge__amount">${raw.replace(/₹/g, "")}</span>`;
  }
  const match = raw.match(/^([^\d]*)([\d].*)$/);
  if (match && match[1]) {
    return `<span class="shiksha-price-badge__currency">${match[1]}</span><span class="shiksha-price-badge__amount">${match[2]}</span>`;
  }
  return raw;
}

class ProductStickyBar extends Component {
  requiredRefs = ["desktopBar", "mobileBar"];

  #abortController = new AbortController();
  #scrollObserver = null;
  #intersectionObserver = null;
  #mainAtcButton = /** @type {HTMLElement | null} */ (null);
  #isMobile = false;
  #pendingCartUpdates = null;
  #root = /** @type {HTMLElement | null} */ (null);
  #lastMobileStickyBarHeight = -1;

  connectedCallback() {
    super.connectedCallback();

    this.#isMobile = window.innerWidth < 750;
    this.#root = document.documentElement;

    // Initially hide desktop bar - it will show when scrolling past main ATC
    if (!this.#isMobile) {
      const desktopBar = /** @type {HTMLElement | null} */ (this.refs.desktopBar);
      desktopBar?.classList.add("hidden");
    }

    this.#setupDesktopVisibility();
    this.#setupMobileVisibility();
    this.#setupBuyNowButtons();
    this.#setupVariantUpdates();
    this.#setupCartUpdates();
    this.#setupMainFormSync();

    // Handle window resize
    window.addEventListener("resize", this.#handleResize, { signal: this.#abortController.signal });

    // Initial check on load and sync cart quantity
    setTimeout(async () => {
      if (!this.#isMobile) {
        this.#updateStickyBarHeight();
        this.#checkDesktopVisibility();
      } else {
        this.#updateMobileStickyBarHeight();
      }
      // Sync initial cart quantity
      const variantId = this.dataset.variantId || this.querySelector('input[name="id"]')?.value;
      if (variantId) {
        await this.#updateCartQuantity(variantId);
      }
    }, 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#scrollObserver) {
      this.#scrollObserver.disconnect();
    }
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }
  }

  /**
   * Setup desktop sticky bar visibility based on scroll position
   */
  #setupDesktopVisibility() {
    if (this.#isMobile) return;

    // Find the buy-buttons-block container (this contains both ATC button and quantity selector)
    const buyButtonsBlock = document.querySelector(".buy-buttons-block");
    if (!buyButtonsBlock) {
      // If buy buttons block not found, keep sticky bar hidden
      const desktopBar = /** @type {HTMLElement | null} */ (this.refs.desktopBar);
      desktopBar?.classList.add("hidden");
      return;
    }

    // Store reference to buy buttons block for visibility checking
    this.#mainAtcButton = buyButtonsBlock;

    // Use Intersection Observer to detect when buy-buttons-block is visible
    // Note: We still use scroll handler for precise control, but IntersectionObserver as backup
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Use scroll handler for more precise control
          this.#checkDesktopVisibility();
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      },
    );

    if (buyButtonsBlock) {
      this.#intersectionObserver.observe(buyButtonsBlock);
    }

    // Also check on scroll for better performance
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.#checkDesktopVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { signal: this.#abortController.signal });

    // Initial check
    this.#checkDesktopVisibility();
  }

  /**
   * Update sticky bar height and set CSS custom property
   */
  #updateStickyBarHeight() {
    if (this.#isMobile) return 0;
    const desktopBar = /** @type {HTMLElement | null} */ (this.refs.desktopBar);
    if (!desktopBar || !this.#root) return 0;
    const height = Math.round(desktopBar.offsetHeight);
    this.#root.style.setProperty("--bottomBarHeight", `${height}px`);
    return height;
  }

  /**
   * Update mobile sticky bar height and set CSS custom property
   */
  #updateMobileStickyBarHeight() {
    if (!this.#isMobile) return 0;
    const mobileBar = /** @type {HTMLElement | null} */ (this.refs.mobileBar);
    if (!mobileBar || !this.#root) return 0;
    // Defer the offsetHeight read (forces layout) + custom property write to the next
    // animation frame so this doesn't force a synchronous style/layout recalculation
    // on the caller's stack.
    window.requestAnimationFrame(() => {
      const height = Math.round(mobileBar.offsetHeight);
      // Skip the style mutation (and the style recalc it triggers) when the height hasn't changed
      if (height === this.#lastMobileStickyBarHeight) return;
      this.#lastMobileStickyBarHeight = height;
      this.#root?.style.setProperty("--mobileStickyBarHeight", `${height}px`);
    });
    return 0;
  }

  /**
   * Check if desktop sticky bar should be visible
   */
  #checkDesktopVisibility() {
    if (this.#isMobile || !this.#mainAtcButton) {
      const desktopBar = /** @type {HTMLElement | null} */ (this.refs.desktopBar);
      desktopBar?.classList.add("hidden");
      desktopBar?.classList.remove("stickybar-active");
      return;
    }

    const desktopBar = /** @type {HTMLElement | null} */ (this.refs.desktopBar);
    let visibilityTarget = this.#mainAtcButton;
    const bundleSelected = document.querySelector('.superbundle-selector input[name="superbundle-option"][value="bundle"]:checked');
    if (bundleSelected) {
      const bundleWrapper = document.querySelector('.superbundle-atc-wrapper');
      if (bundleWrapper) {
        visibilityTarget = bundleWrapper;
      }
    }

    const rect = visibilityTarget.getBoundingClientRect();

    // Check if buy-buttons-block has scrolled out of viewport (bottom < 0)
    // This works whether the ATC button or quantity selector is visible
    const isScrolledOut = rect.bottom < 0;

    if (isScrolledOut) {
      // buy-buttons-block is scrolled out of viewport, show sticky bar
      desktopBar?.classList.remove("hidden");
      desktopBar?.classList.add("stickybar-active");

      // Update sticky bar height
      this.#updateStickyBarHeight();
    } else {
      // buy-buttons-block is visible, hide sticky bar
      desktopBar?.classList.add("hidden");
      desktopBar?.classList.remove("stickybar-active");
    }
  }

  /**
   * Setup mobile sticky bar (always visible)
   */
  #setupMobileVisibility() {
    const mobileBar = /** @type {HTMLElement | null} */ (this.refs.mobileBar);
    if (!this.#isMobile) {
      mobileBar?.classList.add("hidden");
      return;
    }

    // Mobile bar is always visible, ensure it's shown
    mobileBar?.classList.remove("hidden");

    // Update mobile sticky bar height
    setTimeout(() => {
      this.#updateMobileStickyBarHeight();
    }, 100);
  }

  /**
   * Setup Buy Now button functionality
   */
  #setupBuyNowButtons() {
    const buyNowButtons = this.querySelectorAll('[ref="buyNowButton"]');
    buyNowButtons.forEach((button) => {
      button.addEventListener("click", this.#handleBuyNow, { signal: this.#abortController.signal });
    });
  }

  /**
   * Handle Buy Now button click
   */
  #handleBuyNow = async (event) => {
    event.preventDefault();
    const button = /** @type {HTMLButtonElement | null} */ (event.target.closest('[ref="buyNowButton"]'));
    if (!button || button.disabled) return;

    const form = /** @type {HTMLFormElement | null} */ (button.closest("form"));
    if (!form) return;

    // Get the product form component
    const productForm = button.closest("product-form-component");
    if (!productForm) return;

    // Disable button during processing
    button.disabled = true;

    try {
      // First, add to cart
      const formData = new FormData(form);
      const variantId = formData.get("id");
      const quantity = formData.get("quantity") || this.dataset.quantityDefault || "1";

      if (!variantId) {
        console.error("Variant ID is required");
        button.disabled = false;
        return;
      }

      const addToCartResponse = await fetch(Theme.routes.cart_add_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: new URLSearchParams({
          id: variantId.toString(),
          quantity: quantity.toString(),
        }),
      });

      const addToCartResult = await addToCartResponse.json();

      if (addToCartResult.status) {
        // Error adding to cart
        console.error("Error adding to cart:", addToCartResult.message);
        button.disabled = false;
        return;
      }

      // Dispatch cart update event (this will trigger sync in main form via cart update listener)
      document.dispatchEvent(
        new CartAddEvent({}, variantId.toString(), {
          source: "product-sticky-bar",
          itemCount: Number(quantity),
          productId: this.dataset.productId,
        }),
      );

      // Update sticky bar UI immediately
      await this.#updateCartQuantity(variantId.toString());

      // Sync main form after a short delay
      setTimeout(() => {
        this.#syncMainForm(variantId.toString());
      }, 300);

      // Redirect to checkout
      if (Theme?.routes?.cart_url) {
        window.location.href = Theme.routes.cart_url + "?checkout";
      } else {
        window.location.href = "/checkout";
      }
    } catch (error) {
      console.error("Error in Buy Now:", error);
      button.disabled = false;
    }
  };

  /**
   * Setup variant update listeners
   */
  #setupVariantUpdates() {
    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  /**
   * Handle variant updates
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const newVariantId = event.detail.resource?.id;
    if (!newVariantId) return;

    // Update variant ID data attribute
    this.dataset.variantId = newVariantId.toString();

    // Update variant ID in all forms
    const variantInputs = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[name="id"]'));
    variantInputs.forEach((input) => {
      input.value = newVariantId.toString();
    });

    // Update buy now button state
    const buyNowButtons = /** @type {NodeListOf<HTMLButtonElement>} */ (this.querySelectorAll('[ref="buyNowButton"]'));
    const isAvailable = event.detail.resource != null && event.detail.resource.available !== false;
    this.dataset.variantAvailable = isAvailable ? "true" : "false";
    buyNowButtons.forEach((button) => {
      button.disabled = !isAvailable;
    });

    // Update price, compare price, discount, and stock status from updated HTML
    this.#updatePriceAndStock(event);

    // Morph sticky ATC buttons to Sold Out / Add to Bag for the new variant
    this.#updateStickyAddToCartButtons(event, isAvailable);

    // Sold out: force ATC UI immediately (never keep previous variant's quantity controls)
    if (!isAvailable) {
      this.#setStickyBuyButtonsVisibility(0, false);
    }

    // Update cart quantity for new variant
    await this.#updateCartQuantity(newVariantId.toString());
  };

  /**
   * Morph sticky ATC buttons to match the selected variant (Sold Out vs Add to Bag).
   * @param {VariantUpdateEvent} event
   * @param {boolean} isAvailable
   */
  #updateStickyAddToCartButtons(event, isAvailable) {
    const updatedHtml = event.detail.data.html;
    const newAddToCartButton = updatedHtml?.querySelector('[ref="addToCartButton"]');
    const stickyButtons = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[ref="addToCartButton"]'));

    stickyButtons.forEach((button) => {
      if (newAddToCartButton) {
        morph(button, newAddToCartButton);
      }
      if (button instanceof HTMLButtonElement) {
        button.disabled = !isAvailable;
      }
      const container = button.closest("add-to-cart-component");
      if (container && typeof /** @type {any} */ (container).disable === "function") {
        if (isAvailable) {
          /** @type {any} */ (container).enable?.();
        } else {
          /** @type {any} */ (container).disable?.();
        }
      }
    });
  }

  /**
   * Show ATC or quantity selector in the sticky bar based on cart qty + availability.
   * @param {number} cartQty
   * @param {boolean} [available]
   */
  #setStickyBuyButtonsVisibility(cartQty, available = this.dataset.variantAvailable !== "false") {
    const showQuantity = available !== false && cartQty > 0;
    const effectiveCartQty = showQuantity ? cartQty : 0;

    const atcWrappers = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[ref="addToCartButtonWrapper"]'));
    const quantityWrappers = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[ref="productQuantitySelectorWrapper"]'));

    if (!showQuantity) {
      atcWrappers.forEach((wrapper) => {
        wrapper.classList.remove("hidden");
        wrapper.hidden = false;
        wrapper.setAttribute("data-cart-quantity", "0");
      });
      quantityWrappers.forEach((wrapper) => {
        wrapper.classList.add("hidden");
        wrapper.hidden = true;
        wrapper.setAttribute("data-cart-quantity", "0");
        const quantitySelector = /** @type {any} */ (wrapper.querySelector("product-quantity-selector-component"));
        quantitySelector?.setCartQuantity?.(0);
      });
    } else {
      quantityWrappers.forEach((wrapper) => {
        const quantitySelector = /** @type {any} */ (wrapper.querySelector("product-quantity-selector-component"));
        wrapper.classList.remove("hidden");
        wrapper.hidden = false;
        wrapper.setAttribute("data-cart-quantity", effectiveCartQty.toString());
        if (quantitySelector?.setCartQuantity) {
          const currentValue = parseInt(quantitySelector.refs?.quantityInput?.value || "0");
          const currentCartQty = parseInt(quantitySelector.dataset?.cartQuantity || "0");
          if (currentCartQty !== effectiveCartQty || currentValue !== effectiveCartQty) {
            quantitySelector.setCartQuantity(effectiveCartQty);
          }
        }
      });
      atcWrappers.forEach((wrapper) => {
        wrapper.classList.add("hidden");
        wrapper.hidden = true;
        wrapper.setAttribute("data-cart-quantity", effectiveCartQty.toString());
      });
    }

    if (this.#isMobile) {
      setTimeout(() => {
        this.#updateMobileStickyBarHeight();
      }, 100);
    }

    if (!this.#isMobile) {
      setTimeout(() => {
        this.#checkDesktopVisibility();
      }, 100);
    }
  }

  /**
   * Update price, compare price, discount, and stock status from variant update event
   */
  #updatePriceAndStock(event) {
    const updatedHtml = event.detail.data.html;
    if (!updatedHtml) return;

    // Try to find the updated sticky bar in the HTML
    let updatedStickyBar = updatedHtml.querySelector(`product-sticky-bar[data-product-id="${this.dataset.productId}"]`);

    // If sticky bar not found in updated HTML, try to get price from product-price component
    if (!updatedStickyBar) {
      const updatedProductPrice = updatedHtml.querySelector('product-price [ref="priceContainer"]');
      if (updatedProductPrice) {
        // Extract price from product-price component as fallback
        this.#updatePriceFromProductPrice(updatedProductPrice);
      }
      return;
    }

    // Update desktop pricing
    const desktopPrice = updatedStickyBar.querySelector(".product-sticky-bar__price");
    const desktopComparePrice = updatedStickyBar.querySelector(".product-sticky-bar__compare-price");
    const desktopDiscount = updatedStickyBar.querySelector(".product-sticky-bar__discount");

    const currentDesktopPrice = this.querySelector(".product-sticky-bar__price");
    const currentDesktopComparePrice = this.querySelector(".product-sticky-bar__compare-price");
    const currentDesktopDiscount = this.querySelector(".product-sticky-bar__discount");
    const currentDesktopPricing = this.querySelector(".product-sticky-bar__pricing");

    if (currentDesktopPrice && desktopPrice) {
      const desktopPriceText = desktopPrice.textContent.trim();
      if (this.classList.contains("product-sticky-bar--shiksha")) {
        let inner = currentDesktopPrice.querySelector(".shiksha-price-badge__text");
        if (!inner) {
          currentDesktopPrice.innerHTML = '<span class="shiksha-price-badge__text"></span>';
          inner = currentDesktopPrice.querySelector(".shiksha-price-badge__text");
        }
        if (inner) inner.innerHTML = formatShikshaBadgePrice(desktopPriceText);
      } else {
        currentDesktopPrice.textContent = desktopPriceText;
      }
    }

    if (desktopComparePrice && currentDesktopComparePrice) {
      currentDesktopComparePrice.textContent = desktopComparePrice.textContent;
    } else if (!desktopComparePrice && currentDesktopComparePrice) {
      currentDesktopComparePrice.remove();
    } else if (desktopComparePrice && !currentDesktopComparePrice && currentDesktopPricing) {
      // Insert compare price if it doesn't exist
      const newComparePrice = desktopComparePrice.cloneNode(true);
      currentDesktopPrice?.insertAdjacentElement("afterend", newComparePrice);
    }

    syncMrpLabel(
      updatedStickyBar.querySelector(".product-sticky-bar__pricing"),
      currentDesktopPricing,
      "leading",
      ".product-sticky-bar__price"
    );
    syncMrpLabel(
      updatedStickyBar.querySelector(".product-sticky-bar__pricing"),
      currentDesktopPricing,
      "compare",
      ".product-sticky-bar__compare-price"
    );

    if (desktopDiscount && currentDesktopDiscount) {
      if (this.classList.contains("product-sticky-bar--flash-sale")) {
        currentDesktopDiscount.replaceWith(desktopDiscount.cloneNode(true));
      } else {
        currentDesktopDiscount.textContent = desktopDiscount.textContent;
      }
    } else if (!desktopDiscount && currentDesktopDiscount) {
      currentDesktopDiscount.remove();
    } else if (desktopDiscount && !currentDesktopDiscount && currentDesktopPricing) {
      // Insert discount if it doesn't exist
      const newDiscount = desktopDiscount.cloneNode(true);
      const comparePrice = this.querySelector(".product-sticky-bar__compare-price");
      (comparePrice || currentDesktopPrice)?.insertAdjacentElement("afterend", newDiscount);
    }

    // Update mobile pricing
    const mobilePrice = updatedStickyBar.querySelector(".product-sticky-bar__mobile-price");
    const mobileComparePrice = updatedStickyBar.querySelector(".product-sticky-bar__mobile-compare-price");
    const mobileDiscount = updatedStickyBar.querySelector(".product-sticky-bar__mobile-discount");
    const mobileStockStatus = updatedStickyBar.querySelector(".product-sticky-bar__stock-status");

    const currentMobilePrice = this.querySelector(".product-sticky-bar__mobile-price");
    const currentMobileComparePrice = this.querySelector(".product-sticky-bar__mobile-compare-price");
    const currentMobileDiscount = this.querySelector(".product-sticky-bar__mobile-discount");
    const currentMobileStockStatus = this.querySelector(".product-sticky-bar__stock-status");
    const currentMobilePriceInfo = this.querySelector(".product-sticky-bar__mobile-price-info");
    const currentMobilePricing = this.querySelector(".product-sticky-bar__mobile-pricing");

    if (currentMobilePrice && mobilePrice) {
      const mobilePriceText = mobilePrice.textContent.trim();
      if (this.classList.contains("product-sticky-bar--shiksha")) {
        let inner = currentMobilePrice.querySelector(".shiksha-price-badge__text");
        if (!inner) {
          currentMobilePrice.innerHTML = '<span class="shiksha-price-badge__text"></span>';
          inner = currentMobilePrice.querySelector(".shiksha-price-badge__text");
        }
        if (inner) inner.innerHTML = formatShikshaBadgePrice(mobilePriceText);
      } else {
        currentMobilePrice.textContent = mobilePriceText;
      }
    }

    if (mobileComparePrice && currentMobileComparePrice) {
      currentMobileComparePrice.textContent = mobileComparePrice.textContent;
    } else if (!mobileComparePrice && currentMobileComparePrice) {
      currentMobileComparePrice.remove();
    } else if (mobileComparePrice && !currentMobileComparePrice && currentMobilePriceInfo) {
      const newComparePrice = mobileComparePrice.cloneNode(true);
      currentMobilePrice?.insertAdjacentElement("afterend", newComparePrice);
    }

    const updatedMobilePriceInfo = updatedStickyBar.querySelector(".product-sticky-bar__mobile-price-info");
    syncMrpLabel(updatedMobilePriceInfo, currentMobilePriceInfo, "leading", ".product-sticky-bar__mobile-price");
    syncMrpLabel(updatedMobilePriceInfo, currentMobilePriceInfo, "compare", ".product-sticky-bar__mobile-compare-price");

    if (mobileDiscount && currentMobileDiscount) {
      if (this.classList.contains("product-sticky-bar--flash-sale")) {
        currentMobileDiscount.replaceWith(mobileDiscount.cloneNode(true));
      } else {
        currentMobileDiscount.textContent = mobileDiscount.textContent;
      }
    } else if (!mobileDiscount && currentMobileDiscount) {
      currentMobileDiscount.remove();
    } else if (mobileDiscount && !currentMobileDiscount && currentMobilePriceInfo) {
      const newDiscount = mobileDiscount.cloneNode(true);
      const infoWrap = this.querySelector(".product-sticky-bar__mobile-price-info .price-info-wrap-pdp");
      const comparePrice = this.querySelector(".product-sticky-bar__mobile-compare-price");
      (infoWrap || comparePrice || currentMobilePrice)?.insertAdjacentElement("afterend", newDiscount);
    }

    // Sync price details tooltip (info icon + popover)
    const mobileInfoWrap = updatedStickyBar.querySelector(".product-sticky-bar__mobile-price-info .price-info-wrap-pdp");
    const currentMobileInfoWrap = this.querySelector(".product-sticky-bar__mobile-price-info .price-info-wrap-pdp");
    if (mobileInfoWrap && currentMobileInfoWrap) {
      currentMobileInfoWrap.replaceWith(mobileInfoWrap.cloneNode(true));
    } else if (!mobileInfoWrap && currentMobileInfoWrap) {
      currentMobileInfoWrap.remove();
    } else if (mobileInfoWrap && !currentMobileInfoWrap && currentMobilePriceInfo) {
      const newInfoWrap = mobileInfoWrap.cloneNode(true);
      const comparePrice = this.querySelector(".product-sticky-bar__mobile-compare-price");
      (comparePrice || currentMobilePrice)?.insertAdjacentElement("afterend", newInfoWrap);
    }

    // Update stock status
    if (mobileStockStatus) {
      if (currentMobileStockStatus) {
        // Update existing stock status
        currentMobileStockStatus.textContent = mobileStockStatus.textContent;
        currentMobileStockStatus.className = mobileStockStatus.className;
      } else if (currentMobilePricing) {
        // Add stock status if it doesn't exist
        const newStockStatus = mobileStockStatus.cloneNode(true);
        currentMobilePricing.appendChild(newStockStatus);
      }
    } else if (currentMobileStockStatus) {
      // Remove stock status if it's not in updated HTML
      currentMobileStockStatus.remove();
    }
  }

  /**
   * Fallback: Update price from product-price component
   */
  #updatePriceFromProductPrice(priceContainer) {
    // Extract price text from product-price component
    const mainPrice = priceContainer.querySelector(".main-price-pdp, .main-price, .price");
    const comparePrice = priceContainer.querySelector(".compare-at-price-pdp, .compare-at-price");
    const discount = priceContainer.querySelector(".discount-pdp, .discount-container");
    const stockStatus = priceContainer.querySelector(".stock-status-pdp");

    // Update mobile price
    const currentMobilePrice = this.querySelector(".product-sticky-bar__mobile-price");
    if (currentMobilePrice && mainPrice) {
      const priceText = mainPrice.textContent.trim();
      if (this.classList.contains("product-sticky-bar--shiksha")) {
        let inner = currentMobilePrice.querySelector(".shiksha-price-badge__text");
        if (!inner) {
          currentMobilePrice.innerHTML = '<span class="shiksha-price-badge__text"></span>';
          inner = currentMobilePrice.querySelector(".shiksha-price-badge__text");
        }
        if (inner) inner.innerHTML = formatShikshaBadgePrice(priceText);
      } else {
        currentMobilePrice.textContent = priceText;
      }
    }

    // Update mobile compare price
    const currentMobileComparePrice = this.querySelector(".product-sticky-bar__mobile-compare-price");
    const currentMobilePriceInfo = this.querySelector(".product-sticky-bar__mobile-price-info");
    if (comparePrice) {
      if (currentMobileComparePrice) {
        currentMobileComparePrice.textContent = comparePrice.textContent.trim();
      } else if (currentMobilePriceInfo) {
        const newComparePrice = document.createElement("span");
        newComparePrice.className = "product-sticky-bar__mobile-compare-price";
        newComparePrice.textContent = comparePrice.textContent.trim();
        currentMobilePrice?.insertAdjacentElement("afterend", newComparePrice);
      }
    } else if (currentMobileComparePrice) {
      currentMobileComparePrice.remove();
    }

    const sourceMain = priceContainer.querySelector(".price-main-pdp") || priceContainer;
    const sourceCompare = priceContainer.querySelector(".compare-price-pdp") || priceContainer;
    syncMrpLabel(sourceMain, currentMobilePriceInfo, "leading", ".product-sticky-bar__mobile-price");
    syncMrpLabel(comparePrice ? sourceCompare : priceContainer, currentMobilePriceInfo, "compare", ".product-sticky-bar__mobile-compare-price");

    // Update mobile discount
    const currentMobileDiscount = this.querySelector(".product-sticky-bar__mobile-discount");
    if (discount) {
      if (this.classList.contains("product-sticky-bar--flash-sale") && discount.classList.contains("discount-pdp--flash-sale")) {
        const sourceText = discount.querySelector(".flash-sale-discount__text");
        if (currentMobileDiscount) {
          let textEl = currentMobileDiscount.querySelector(".flash-sale-discount__text");
          if (textEl && sourceText) {
            textEl.innerHTML = sourceText.innerHTML;
          } else if (textEl) {
            const flashText = sourceText?.textContent?.trim() || discount.textContent.trim().replace(/[()]/g, "").trim();
            textEl.innerHTML = flashText.replace(/OFF\s*$/i, '<span class="flash-sale-discount__off">OFF</span>');
          } else {
            currentMobileDiscount.replaceWith(discount.cloneNode(true));
            const replaced = this.querySelector(".discount-pdp--flash-sale, .product-sticky-bar__mobile-discount");
            if (replaced && !replaced.classList.contains("product-sticky-bar__mobile-discount")) {
              replaced.className = "product-sticky-bar__mobile-discount";
            }
          }
        } else if (currentMobilePriceInfo) {
          const newDiscount = document.createElement("span");
          newDiscount.className = "product-sticky-bar__mobile-discount";
          newDiscount.innerHTML = discount.innerHTML;
          const infoWrap = this.querySelector(".product-sticky-bar__mobile-price-info .price-info-wrap-pdp");
          const comparePriceEl = this.querySelector(".product-sticky-bar__mobile-compare-price");
          (infoWrap || comparePriceEl || currentMobilePrice)?.insertAdjacentElement("afterend", newDiscount);
        }
      } else {
        const discountText = discount.textContent.trim();
        if (currentMobileDiscount) {
          currentMobileDiscount.textContent = discountText;
        } else if (currentMobilePriceInfo) {
          const newDiscount = document.createElement("span");
          newDiscount.className = "product-sticky-bar__mobile-discount";
          newDiscount.textContent = discountText;
          const infoWrap = this.querySelector(".product-sticky-bar__mobile-price-info .price-info-wrap-pdp");
          const comparePriceEl = this.querySelector(".product-sticky-bar__mobile-compare-price");
          (infoWrap || comparePriceEl || currentMobilePrice)?.insertAdjacentElement("afterend", newDiscount);
        }
      }
    } else if (currentMobileDiscount) {
      currentMobileDiscount.remove();
    }

    // Sync tooltip values from PDP price container
    const sourceTooltip = priceContainer.querySelector(".price-details-tooltip-pdp");
    const currentInfoWrap = this.querySelector(".product-sticky-bar__mobile-price-info .price-info-wrap-pdp");
    const sourceInfoWrap = priceContainer.querySelector(".price-info-wrap-pdp");

    if (sourceInfoWrap && currentInfoWrap) {
      const mrpValue = sourceTooltip?.querySelector('[data-price-detail="mrp"]')?.textContent?.trim()
        || comparePrice?.textContent?.trim().replace(/^MRP\s*/i, "");
      const sellingValue = sourceTooltip?.querySelector('[data-price-detail="selling"]')?.textContent?.trim()
        || mainPrice?.textContent?.trim();
      const discountValue = sourceTooltip?.querySelector('[data-price-detail="discount"]')?.textContent?.trim()
        || discount?.textContent?.trim().replace(/[()]/g, "").trim();

      const currentMrp = currentInfoWrap.querySelector('[data-price-detail="mrp"]');
      const currentSelling = currentInfoWrap.querySelector('[data-price-detail="selling"]');
      const currentDiscountDetail = currentInfoWrap.querySelector('[data-price-detail="discount"]');

      if (currentMrp && mrpValue) currentMrp.textContent = mrpValue;
      if (currentSelling && sellingValue) currentSelling.textContent = sellingValue;
      if (currentDiscountDetail && discountValue) currentDiscountDetail.textContent = discountValue;

      const currentTooltip = currentInfoWrap.querySelector(".price-details-tooltip-pdp");
      if (currentTooltip && sourceTooltip) {
        currentTooltip.classList.toggle(
          "price-details-tooltip-pdp--mrp-only",
          sourceTooltip.classList.contains("price-details-tooltip-pdp--mrp-only")
        );
      }
    } else if (!sourceInfoWrap && currentInfoWrap) {
      currentInfoWrap.remove();
    } else if (sourceInfoWrap && !currentInfoWrap && currentMobilePriceInfo) {
      const newInfoWrap = sourceInfoWrap.cloneNode(true);
      const trigger = newInfoWrap.querySelector("[data-price-info-trigger]");
      if (trigger) {
        trigger.removeAttribute("data-bound");
        trigger.setAttribute("aria-controls", `sticky-price-details-tooltip-${this.dataset.productId}`);
      }
      const tooltip = newInfoWrap.querySelector(".price-details-tooltip-pdp");
      if (tooltip) tooltip.id = `sticky-price-details-tooltip-${this.dataset.productId}`;
      const comparePriceEl = this.querySelector(".product-sticky-bar__mobile-compare-price");
      (comparePriceEl || currentMobilePrice)?.insertAdjacentElement("afterend", newInfoWrap);
    }

    // Update stock status
    const currentMobileStockStatus = this.querySelector(".product-sticky-bar__stock-status");
    const currentMobilePricing = this.querySelector(".product-sticky-bar__mobile-pricing");
    if (stockStatus) {
      const stockText = stockStatus.textContent.trim();
      const stockClass = stockStatus.className.includes("out_of_stock") ? "out_of_stock" : "in_stock";
      if (currentMobileStockStatus) {
        currentMobileStockStatus.textContent = stockText;
        currentMobileStockStatus.className = `product-sticky-bar__stock-status product-sticky-bar__stock-status--${stockClass}`;
      } else if (currentMobilePricing) {
        const newStockStatus = document.createElement("span");
        newStockStatus.className = `product-sticky-bar__stock-status product-sticky-bar__stock-status--${stockClass}`;
        newStockStatus.textContent = stockText;
        currentMobilePricing.appendChild(newStockStatus);
      }
    }
  }

  /**
   * Setup cart update listeners
   */
  #setupCartUpdates() {
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    document.addEventListener(ThemeEvents.cartAdd, this.#onCartUpdate, { signal });
  }

  /**
   * Setup syncing with main product form
   */
  #setupMainFormSync() {
    const { signal } = this.#abortController;
    // Track pending updates to prevent race conditions
    this.#pendingCartUpdates = new Map();

    // Listen for quantity change events from main form and sticky bar
    document.addEventListener("product-quantity-change", this.#onQuantityChange, { signal });

    // Use MutationObserver to watch for changes in main form
    const mainBuyButtonsBlock = document.querySelector(".buy-buttons-block");
    if (mainBuyButtonsBlock) {
      const observer = new MutationObserver(() => {
        // When main form changes, sync sticky bar
        const variantId = this.dataset.variantId || this.querySelector('input[name="id"]')?.value;
        if (variantId) {
          setTimeout(() => {
            this.#updateCartQuantity(variantId);
          }, 100);
        }
        // Recheck desktop visibility when main form changes (ATC button might have been hidden/shown)
        if (!this.#isMobile) {
          setTimeout(() => {
            this.#checkDesktopVisibility();
          }, 150);
        }
      });

      observer.observe(mainBuyButtonsBlock, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "hidden", "data-cart-quantity"],
      });

      // Cleanup on disconnect
      this.#abortController.signal.addEventListener("abort", () => {
        observer.disconnect();
      });
    }

    // Also listen to cart update events
    document.addEventListener(
      ThemeEvents.cartUpdate,
      (event) => {
        if (shouldSkipBuyNowCartSync(event)) return;
        const variantId = this.dataset.variantId || this.querySelector('input[name="id"]')?.value;
        if (variantId) {
          setTimeout(() => {
            this.#updateCartQuantity(variantId);
          }, 200);
        }
      },
      { signal },
    );

    // Listen for quantity selector component events
    const checkQuantitySelectors = () => {
      const mainQuantitySelector = document.querySelector(".buy-buttons-block product-quantity-selector-component");
      const stickyQuantitySelectors = this.querySelectorAll("product-quantity-selector-component");

      // Sync main to sticky - listen to input changes
      if (mainQuantitySelector) {
        const handleMainChange = () => {
          const variantId = this.dataset.variantId || this.querySelector('input[name="id"]')?.value;
          if (variantId) {
            setTimeout(() => {
              this.#updateCartQuantity(variantId);
            }, 100);
          }
        };

        mainQuantitySelector.addEventListener("input", handleMainChange, { signal });
        mainQuantitySelector.addEventListener("change", handleMainChange, { signal });
      }

      // Sync sticky to main - DON'T listen to input/change events from sticky bar quantity selectors
      // These are handled by product-quantity-change events to avoid race conditions
      // The quantity selector component itself handles the cart update and dispatches events
    };

    // Check immediately and after a delay to catch dynamically loaded elements
    checkQuantitySelectors();
    setTimeout(checkQuantitySelectors, 500);
  }

  /**
   * Handle quantity change events for bidirectional syncing
   * @param {CustomEvent} event
   */
  #onQuantityChange = (event) => {
    const variantId = this.dataset.variantId || this.querySelector('input[name="id"]')?.value;
    if (!variantId || variantId.toString() !== event.detail.variantId.toString()) return;

    // Check if event came from sticky bar or main form
    const source = event.detail.source;
    const stickySelectors = this.querySelectorAll("product-quantity-selector-component");
    const mainSelector = document.querySelector(".buy-buttons-block product-quantity-selector-component");

    // If event came from sticky bar's own quantity selector, skip syncing
    // The quantity selector handles its own cart update and will dispatch cart update events
    // which will trigger proper syncing without causing flicker
    const isFromSticky = Array.from(stickySelectors).some((sel) => sel === source);
    if (isFromSticky) {
      // Don't sync immediately - let the quantity selector complete its cart update
      // The cart update event will handle syncing
      return;
    }

    // If event came from main form, sync sticky bar
    const isFromMain = mainSelector === source;
    if (isFromMain) {
      // Sync sticky bar UI visibility after a delay to let cart update complete
      setTimeout(() => {
        this.#updateCartQuantity(variantId);
      }, 150);
    }
  };

  /**
   * Handle cart updates
   */
  #onCartUpdate = async (event) => {
    if (shouldSkipBuyNowCartSync(event)) return;
    // Skip optimistic events — real data comes on confirmation
    if (event?.detail?.data?.isOptimistic) return;

    // Skip if this event came from sticky bar to avoid loops (sticky bar handles its own updates)
    if (event?.detail?.source === "product-sticky-bar" || event?.detail?.data?.source === "product-sticky-bar") {
      return;
    }

    // Skip if event came from product-quantity-selector in sticky bar
    // But allow it if it's from main form's quantity selector (we need to sync sticky bar)
    const stickySelectors = this.querySelectorAll("product-quantity-selector-component");
    const eventSourceId = event?.detail?.sourceId;
    if (eventSourceId) {
      const isFromStickySelector = Array.from(stickySelectors).some((sel) => sel.id === eventSourceId);
      if (isFromStickySelector) {
        // Event came from sticky bar's own selector - it already updated itself
        // Just sync visibility state without calling setCartQuantity again
        const variantId = this.dataset.variantId || this.querySelector('input[name="id"]')?.value;
        if (variantId) {
          // Only update visibility, not the quantity selector value (it's already correct)
          await this.#updateCartQuantityVisibilityOnly(variantId);
        }
        // Recheck desktop visibility after cart update (main ATC might have been hidden/shown)
        if (!this.#isMobile) {
          setTimeout(() => {
            this.#checkDesktopVisibility();
          }, 100);
        }
        return;
      }
    }

    const variantId = this.dataset.variantId || this.querySelector('input[name="id"]')?.value;
    if (variantId) {
      await this.#updateCartQuantity(variantId);
      // Recheck desktop visibility after cart update (main ATC might have been hidden/shown)
      if (!this.#isMobile) {
        setTimeout(() => {
          this.#checkDesktopVisibility();
        }, 100);
      }
    }
  };

  /**
   * Sync main form with sticky bar changes
   * @param {string} variantId - The variant ID
   * @param {boolean} skipStickyUpdate - If true, skip updating sticky bar quantity selectors
   */
  async #syncMainForm(variantId, skipStickyUpdate = false) {
    try {
      const cart = await getCart();

      const cartItem = cart.items.find((/** @type {any} */ item) => item.variant_id.toString() === variantId.toString());
      const cartQty = cartItem ? cartItem.quantity : 0;
      const isAvailable = this.dataset.variantAvailable !== "false";
      const showQuantity = isAvailable && cartQty > 0;
      const effectiveCartQty = showQuantity ? cartQty : 0;

      // Find main form's product-form-component and use its method to sync
      const mainFormComponent = document.querySelector(".buy-buttons-block product-form-component");
      if (mainFormComponent && typeof mainFormComponent.querySelector === "function") {
        // Use the main form's own sync method if available
        // This ensures proper UI updates
        const mainFormButtons = mainFormComponent.querySelector('[ref="productFormButtons"]');
        if (mainFormButtons) {
          const mainAtcWrapper = mainFormButtons.querySelector('[ref="addToCartButtonWrapper"]');
          const mainQuantityWrapper = mainFormButtons.querySelector('[ref="productQuantitySelectorWrapper"]');

          // Show ATC button first, then hide quantity selector to prevent white space
          if (!showQuantity) {
            // Show ATC button first (synchronously)
            if (mainAtcWrapper) {
              mainAtcWrapper.classList.remove("hidden");
              mainAtcWrapper.hidden = false;
              mainAtcWrapper.setAttribute("data-cart-quantity", "0");
            }
            // Then immediately hide quantity selector (synchronously)
            if (mainQuantityWrapper) {
              mainQuantityWrapper.classList.add("hidden");
              mainQuantityWrapper.hidden = true;
              mainQuantityWrapper.setAttribute("data-cart-quantity", "0");
            }
          } else {
            // Show quantity selector first (synchronously)
            if (mainQuantityWrapper) {
              const mainQuantitySelector = /** @type {any} */ (mainQuantityWrapper.querySelector("product-quantity-selector-component"));
              mainQuantityWrapper.classList.remove("hidden");
              mainQuantityWrapper.hidden = false;
              mainQuantityWrapper.setAttribute("data-cart-quantity", effectiveCartQty.toString());

              // Update quantity selector if it exists
              if (mainQuantitySelector?.setCartQuantity && !skipStickyUpdate) {
                mainQuantitySelector.setCartQuantity(effectiveCartQty);
              }
            }
            // Then immediately hide ATC button (synchronously)
            if (mainAtcWrapper) {
              mainAtcWrapper.classList.add("hidden");
              mainAtcWrapper.hidden = true;
              mainAtcWrapper.setAttribute("data-cart-quantity", effectiveCartQty.toString());
            }
          }
        }
      } else {
        // Fallback: direct DOM manipulation if main form component not found
        const mainAtcWrapper = document.querySelector(".buy-buttons-block [ref='addToCartButtonWrapper']");
        const mainQuantityWrapper = document.querySelector(".buy-buttons-block [ref='productQuantitySelectorWrapper']");

        if (mainAtcWrapper) {
          if (!showQuantity) {
            mainAtcWrapper.classList.remove("hidden");
            mainAtcWrapper.hidden = false;
          } else {
            mainAtcWrapper.classList.add("hidden");
            mainAtcWrapper.hidden = true;
          }
        }

        if (mainQuantityWrapper) {
          const mainQuantitySelector = /** @type {any} */ (mainQuantityWrapper.querySelector("product-quantity-selector-component"));
          if (!showQuantity) {
            mainQuantityWrapper.classList.add("hidden");
            mainQuantityWrapper.hidden = true;
          } else {
            mainQuantityWrapper.classList.remove("hidden");
            mainQuantityWrapper.hidden = false;
            if (mainQuantitySelector?.setCartQuantity && !skipStickyUpdate) {
              mainQuantitySelector.setCartQuantity(effectiveCartQty);
            }
          }
        }
      }

      // Also update quantity label cart count if it exists
      const mainQuantityLabel = document.querySelector(".buy-buttons-block [ref='quantityLabelCartCount']");
      if (mainQuantityLabel) {
        if (effectiveCartQty === 0) {
          mainQuantityLabel.classList.add("hidden");
        } else {
          mainQuantityLabel.classList.remove("hidden");
          const inCartText = mainQuantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
          if (inCartText && inCartText[2]) {
            mainQuantityLabel.textContent = `(${effectiveCartQty} ${inCartText[2]})`;
          }
        }
      }
    } catch (error) {
      console.error("Failed to sync main form:", error);
    }
  }

  /**
   * Update only visibility state without updating quantity selector values
   * Used when quantity selector has already updated itself
   */
  async #updateCartQuantityVisibilityOnly(variantId) {
    try {
      const cart = await getCart();

      const cartItem = cart.items.find((/** @type {any} */ item) => item.variant_id.toString() === variantId.toString());
      const cartQty = cartItem ? cartItem.quantity : 0;
      const isAvailable = this.dataset.variantAvailable !== "false";

      this.#setStickyBuyButtonsVisibility(cartQty, isAvailable);
    } catch (error) {
      console.error("Failed to update cart quantity visibility:", error);
    }
  }

  /**
   * Update cart quantity and UI
   */
  async #updateCartQuantity(variantId) {
    // Debounce to prevent multiple rapid updates
    if (!this.#pendingCartUpdates) {
      this.#pendingCartUpdates = new Map();
    }

    const updateKey = `update-${variantId}`;
    if (this.#pendingCartUpdates.has(updateKey)) {
      return;
    }

    this.#pendingCartUpdates.set(updateKey, true);

    try {
      const cart = await getCart();

      const cartItem = cart.items.find((/** @type {any} */ item) => item.variant_id.toString() === variantId.toString());
      const cartQty = cartItem ? cartItem.quantity : 0;
      const isAvailable = this.dataset.variantAvailable !== "false";

      this.#setStickyBuyButtonsVisibility(cartQty, isAvailable);
      // Update mobile sticky bar height if on mobile
      if (this.#isMobile) {
        setTimeout(() => {
          this.#updateMobileStickyBarHeight();
        }, 100);
      }

      // Recheck desktop visibility after updating cart quantity
      // The main ATC button might have been hidden/shown, affecting sticky bar visibility
      if (!this.#isMobile) {
        setTimeout(() => {
          this.#checkDesktopVisibility();
        }, 100);
      }
    } catch (error) {
      console.error("Failed to update cart quantity:", error);
    } finally {
      // Clear the pending flag after a short delay
      setTimeout(() => {
        if (this.#pendingCartUpdates) {
          this.#pendingCartUpdates.delete(updateKey);
        }
      }, 300);
    }
  }

  /**
   * Handle window resize
   */
  #handleResize = () => {
    const wasMobile = this.#isMobile;
    this.#isMobile = window.innerWidth < 750;

    if (wasMobile !== this.#isMobile) {
      // Mobile state changed, reinitialize
      this.#setupDesktopVisibility();
      this.#setupMobileVisibility();
    }

    // Update heights on resize
    setTimeout(() => {
      if (!this.#isMobile) {
        this.#updateStickyBarHeight();
        this.#checkDesktopVisibility();
      } else {
        this.#updateMobileStickyBarHeight();
      }
    }, 100);
  };
}

if (!customElements.get("product-sticky-bar")) {
  customElements.define("product-sticky-bar", ProductStickyBar);
}
